import type { StateCreator } from 'zustand';
import type { WorkspaceChat, WorkspaceChatMessage, WorkspacePending, WorkspaceProposal, WorkspaceProposalInput } from '../services/workspace-assistant.types';

type ApiResult = { statusCode: number; data?: WorkspaceChat | null; errors?: unknown[] | null };
type WorkspaceApi = {
  getWorkspaceAssistantChat(): Promise<ApiResult>;
  askWorkspaceAssistant(question: Question, options?: { signal?: AbortSignal }): Promise<ApiResult>;
  clearWorkspaceAssistantChat(): Promise<ApiResult>;
  cancelWorkspaceAssistant(requestId: string): Promise<ApiResult>;
  executeWorkspaceProposal?(proposalId: string, body: WorkspaceProposalInput): Promise<{ statusCode: number; data?: { proposal: WorkspaceProposal } | null; errors?: unknown[] | null }>;
  moreWorkspaceTableRows?(messageId: string, tableId: string): Promise<ApiResult>;
};

export type Question = { message: string; requestId: string; currentScreen: string; locale: string; timezone?: string; attachments?: string[] };
export interface WorkspaceAssistantState {
  scope: string;
  generation: number;
  assistantTab: boolean;
  /** The right pane is showing the assistant (desktop web): the pane widens for it. */
  assistantShown: boolean;
  messages: WorkspaceChatMessage[];
  loading: boolean;
  busy: boolean;
  ready: boolean;
  error: string;
  pending: Question | null;
  failed: Question | null;
  serverPending: WorkspacePending | null;
  /** What the running turn is doing, for the progress line. */
  stage: string;
  activate: (scope: string) => void;
  pollStage: (scope: string) => Promise<void>;
  setAssistantTab: (active: boolean) => void;
  setAssistantShown: (shown: boolean) => void;
  load: (scope: string) => Promise<void>;
  ask: (scope: string, question: Question) => Promise<void>;
  clear: (scope: string) => Promise<void>;
  stop: (scope: string) => Promise<void>;
  execute: (scope: string, proposalId: string, body?: WorkspaceProposalInput) => Promise<void>;
  /** The table loading its next page ("messageId:tableId"), if any. */
  loadingRows: string;
  moreRows: (scope: string, messageId: string, tableId: string) => Promise<void>;
}
const problem = (result: any) => result.errors?.map((e: any) => typeof e === 'string' ? e : e.message).filter(Boolean).join(' ') || 'The assistant is unavailable. Please retry.';
/** In-memory only; server history survives navigation/reload without storing business data in local storage. */
export function workspaceAssistantState(apiService: WorkspaceApi): StateCreator<WorkspaceAssistantState> {
  let activeRequest: AbortController | undefined;
  return (set, get) => ({
    scope: '', generation: 0, assistantTab: false, assistantShown: false, messages: [], loading: false, busy: false, ready: false, error: '', pending: null, failed: null, serverPending: null, stage: '', loadingRows: '',
    activate: scope => { if (get().scope !== scope) { activeRequest?.abort(); set({ scope, generation: get().generation + 1, messages: [], loading: false, busy: false, ready: false, error: '', pending: null, failed: null, serverPending: null, stage: '', loadingRows: '' }); } },
    // While this tab's own question runs: read only what the server is doing now (nothing else changes).
    pollStage: async scope => {
      const requestId = get().pending?.requestId;
      if (get().scope !== scope || !requestId) return;
      try {
        const result = await apiService.getWorkspaceAssistantChat();
        const stage = result.statusCode === 1000 && result.data?.pending?.requestId === requestId ? result.data.pending.stage : undefined;
        if (stage && get().scope === scope && get().pending?.requestId === requestId) set({ stage });
      } catch { /* the progress line is best effort */ }
    },
    setAssistantTab: assistantTab => set({ assistantTab }),
    setAssistantShown: assistantShown => { if (get().assistantShown !== assistantShown) set({ assistantShown }); },
    // The next rows of a reply's table: the server re-runs the same lookup (no new question).
    moreRows: async (scope, messageId, tableId) => {
      if (get().scope !== scope || get().busy || get().serverPending || get().loadingRows || !apiService.moreWorkspaceTableRows) return;
      const generation = get().generation;
      set({ loadingRows: `${messageId}:${tableId}`, error: '' });
      try {
        const result = await apiService.moreWorkspaceTableRows(messageId, tableId);
        if (get().scope !== scope || get().generation !== generation) return;
        if (result.statusCode !== 1000 || !result.data) throw new Error(problem(result));
        set({ messages: result.data.messages });
      } catch (e: any) { if (get().scope === scope && get().generation === generation) set({ error: e.message }); }
      finally { if (get().scope === scope && get().generation === generation) set({ loadingRows: '' }); }
    },
    execute: async (scope, proposalId, body = {}) => {
      if (get().scope !== scope || get().busy || get().loading || get().serverPending || !apiService.executeWorkspaceProposal) return;
      const generation = get().generation;
      set({ busy: true, error: '' });
      try {
        const result = await apiService.executeWorkspaceProposal(proposalId, body);
        if (get().scope !== scope || get().generation !== generation) return;
        if (result.statusCode !== 1000 || !result.data) throw new Error(problem(result));
        const proposal = result.data.proposal;
        set({ messages: get().messages.map(m => m.proposal?.id === proposalId ? { ...m, proposal } : m) });
      } catch (e: any) { if (get().scope === scope && get().generation === generation) set({ error: e.message }); }
      finally { if (get().scope === scope && get().generation === generation) set({ busy: false }); }
    },
    load: async scope => {
      get().activate(scope);
      if (get().loading || get().busy) return;
      const generation = get().generation;
      set({ loading: true, error: '' });
      try {
        const result = await apiService.getWorkspaceAssistantChat();
        if (get().scope !== scope || get().generation !== generation) return;
        if (result.statusCode !== 1000 || !result.data) throw new Error(problem(result));
        set({ messages: result.data.messages, serverPending: result.data.pending || null, stage: result.data.pending?.stage || '', ready: true, failed: null });
      } catch (e: any) { if (get().scope === scope && get().generation === generation) set({ error: e.message, ready: false }); }
      finally { if (get().scope === scope && get().generation === generation) set({ loading: false }); }
    },
    ask: async (scope, question) => {
      if (get().scope !== scope || get().busy || get().loading || get().serverPending) return;
      const generation = get().generation;
      set({ busy: true, pending: question, error: '', failed: null, stage: 'history' });
      const controller = new AbortController(); activeRequest = controller;
      try {
        const result = await apiService.askWorkspaceAssistant(question, { signal: controller.signal });
        if (get().scope !== scope || get().generation !== generation) return;
        if (result.statusCode !== 1000 || !result.data) throw new Error(problem(result));
        set({ messages: result.data.messages, serverPending: result.data.pending || null, ready: true });
      } catch (e: any) {
        if (get().scope === scope && get().generation === generation) {
          set({ error: e.message, failed: question });
          // A second tab or a lost response can leave a valid turn on the server.
          // Recover its state instead of offering repeated submissions into a 409.
          try {
            const current = await apiService.getWorkspaceAssistantChat();
            if (get().scope === scope && get().generation === generation && current.statusCode === 1000 && current.data?.pending) {
              set({ messages: current.data.messages, serverPending: current.data.pending, error: '', failed: null });
            }
          } catch { /* Keep the original failure visible if history is unavailable. */ }
        }
      }
      finally { if (activeRequest === controller) activeRequest = undefined; if (get().scope === scope && get().generation === generation) set({ busy: false, pending: null, stage: '' }); }
    },
    clear: async scope => {
      if (get().scope !== scope || get().busy || get().loading) return;
      const generation = get().generation;
      set({ busy: true, error: '' });
      try {
        const result = await apiService.clearWorkspaceAssistantChat();
        if (get().scope !== scope || get().generation !== generation) return;
        if (result.statusCode !== 1000 || !result.data) throw new Error(problem(result));
        set({ messages: [], pending: null, failed: null, serverPending: null, ready: true });
      } catch (e: any) { if (get().scope === scope && get().generation === generation) set({ error: e.message }); }
      finally { if (get().scope === scope && get().generation === generation) set({ busy: false }); }
    },
    stop: async scope => {
      if (get().scope !== scope) return;
      const requestId = get().pending?.requestId || get().serverPending?.requestId;
      if (!requestId) return;
      const generation = get().generation + 1;
      set({ generation, busy: true, loading: false, error: '' });
      activeRequest?.abort();
      try {
        const result = await apiService.cancelWorkspaceAssistant(requestId);
        if (get().scope !== scope || get().generation !== generation) return;
        if (result.statusCode !== 1000 || !result.data) throw new Error(problem(result));
        set({ messages: result.data.messages, pending: null, serverPending: result.data.pending || null, failed: null, ready: true });
      } catch (e: any) { if (get().scope === scope && get().generation === generation) set({ error: e.message }); }
      finally { if (get().scope === scope && get().generation === generation) set({ busy: false, pending: null }); }
    },
  });
}

